/**
 * Admin API client — wraps /v1/admin/* endpoints.
 * Only usable by platform admins (server enforces).
 */

import { RestClient } from "./restClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DashboardStats {
  users_total: number;
  users_active_24h: number;
  orgs_total: number;
  subscriptions_active: number;
  sponsored_orgs: number;
  schedules_active: number;
}

export interface AdminUser {
  user_id: string;
  email: string | null;
  org_count: number;
  company_ids: string[];
  last_active_at: string | null;
  suspended: boolean;
}

export interface AdminUserDetail {
  user_id: string;
  email: string | null;
  is_platform_admin: boolean;
  memberships: {
    company_id: string;
    company_name: string | null;
    last_active_at: string | null;
    suspended: boolean;
    suspend_reason: string | null;
  }[];
  schedules_active: number;
  sessions_30d: number;
  facts_count: number;
}

export interface AdminOrg {
  id: string;
  name: string;
  created_at: string | null;
  suspended: boolean;
  subscription_status: string | null;
  plan_id: string | null;
  member_count: number;
  seats_used: number | null;
  seat_limit: number | null;
  custom_seat_limit: number | null;
  sponsored_seats: number;
  is_sponsored: boolean;
}

export interface OrgPlatformKnowledge {
  company_id: string;
  allow_platform_knowledge: boolean;
}

export interface AdminOrgDetail {
  id: string;
  name: string;
  created_at: string | null;
  created_by: string | null;
  suspended: boolean;
  suspend_reason: string | null;
  subscription: {
    id: string;
    plan_id: string | null;
    billing_cycle: string;
    status: string;
    seat_limit: number | null;
    seats_used: number | null;
    custom_seat_limit: number | null;
    sponsored_seats: number;
    sponsored_by: string | null;
    sponsored_reason: string | null;
    sponsored_seats_expires_at: string | null;
    sponsored_expires_at: string | null;
    current_period_end: string | null;
  } | null;
  members: {
    user_id: string;
    email: string;
    last_active_at: string | null;
    suspended: boolean;
  }[];
  member_count: number;
  plugin_count: number;
  document_count: number;
  schedule_count: number;
}

export interface PlatformAdmin {
  user_id: string;
  email: string | null;
}

export interface AuditEntry {
  id: string;
  admin_id: string;
  action: string;
  target_type: string;
  target_id: string;
  details: Record<string, unknown>;
  ip_address: string | null;
  created_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class AdminApi {
  constructor(private client: RestClient) {}

  // Dashboard
  async getStats(): Promise<DashboardStats> {
    return this.client.get("/v1/admin/dashboard/stats");
  }

  // Users
  async listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    company_id?: string;
    suspended?: boolean;
  }): Promise<PaginatedResponse<AdminUser>> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.search) qs.set("search", params.search);
    if (params?.company_id) qs.set("company_id", params.company_id);
    if (params?.suspended !== undefined) qs.set("suspended", String(params.suspended));
    const query = qs.toString();
    return this.client.get(`/v1/admin/users${query ? `?${query}` : ""}`);
  }

  async getUserDetail(userId: string): Promise<AdminUserDetail> {
    return this.client.get(`/v1/admin/users/${userId}`);
  }

  async suspendUser(userId: string, reason?: string): Promise<void> {
    await this.client.post(`/v1/admin/users/${userId}/suspend`, { reason });
  }

  async unsuspendUser(userId: string): Promise<void> {
    await this.client.post(`/v1/admin/users/${userId}/unsuspend`);
  }

  async addUserToOrg(userId: string, companyId: string, role = "member"): Promise<void> {
    await this.client.post(`/v1/admin/users/${userId}/add-to-org`, {
      company_id: companyId,
      role,
    });
  }

  async removeUserFromOrg(userId: string, companyId: string): Promise<void> {
    await this.client.post(`/v1/admin/users/${userId}/remove-from-org`, {
      company_id: companyId,
    });
  }

  async changeUserRole(userId: string, companyId: string, role: string): Promise<void> {
    await this.client.post(`/v1/admin/users/${userId}/change-role`, {
      company_id: companyId,
      role,
    });
  }

  async impersonateUser(userId: string): Promise<{ token: string; expires_in: number }> {
    return this.client.post(`/v1/admin/users/${userId}/impersonate`);
  }

  // Orgs
  async listOrgs(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sponsored?: boolean;
  }): Promise<PaginatedResponse<AdminOrg>> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.search) qs.set("search", params.search);
    if (params?.status) qs.set("status", params.status);
    if (params?.sponsored !== undefined) qs.set("sponsored", String(params.sponsored));
    const query = qs.toString();
    return this.client.get(`/v1/admin/orgs${query ? `?${query}` : ""}`);
  }

  async getOrgDetail(companyId: string): Promise<AdminOrgDetail> {
    return this.client.get(`/v1/admin/orgs/${companyId}`);
  }

  async suspendOrg(companyId: string, reason?: string): Promise<void> {
    await this.client.post(`/v1/admin/orgs/${companyId}/suspend`, { reason });
  }

  async unsuspendOrg(companyId: string): Promise<void> {
    await this.client.post(`/v1/admin/orgs/${companyId}/unsuspend`);
  }

  async grantSponsorship(companyId: string, reason: string, expiresAt?: string): Promise<void> {
    await this.client.post(`/v1/admin/orgs/${companyId}/sponsor`, {
      reason,
      expires_at: expiresAt,
    });
  }

  async revokeSponsorship(companyId: string): Promise<void> {
    await this.client.delete(`/v1/admin/orgs/${companyId}/sponsor`);
  }

  async grantSponsoredSeats(companyId: string, count: number, expiresAt?: string): Promise<void> {
    await this.client.post(`/v1/admin/orgs/${companyId}/sponsored-seats`, {
      count,
      expires_at: expiresAt,
    });
  }

  async revokeSponsoredSeats(companyId: string): Promise<void> {
    await this.client.delete(`/v1/admin/orgs/${companyId}/sponsored-seats`);
  }

  async updateSubscription(companyId: string, updates: { plan_id?: string; custom_seat_limit?: number }): Promise<void> {
    await this.client.patch(`/v1/admin/orgs/${companyId}/subscription`, updates);
  }

  // Whether the org sees platform (shared, per-industry) knowledge at all.
  // Platform admins only — the backend enforces it.
  async getPlatformKnowledge(companyId: string): Promise<OrgPlatformKnowledge> {
    return this.client.get(`/v1/admin/orgs/${companyId}/platform-knowledge`);
  }

  async setPlatformKnowledge(
    companyId: string,
    allow: boolean,
  ): Promise<OrgPlatformKnowledge> {
    return this.client.put(`/v1/admin/orgs/${companyId}/platform-knowledge`, {
      allow_platform_knowledge: allow,
    });
  }

  // Platform Admins
  async listPlatformAdmins(): Promise<{ admins: PlatformAdmin[] }> {
    return this.client.get("/v1/admin/platform-admins");
  }

  async addPlatformAdmin(userId?: string, email?: string): Promise<void> {
    await this.client.post("/v1/admin/platform-admins", { user_id: userId, email });
  }

  async removePlatformAdmin(userId: string): Promise<void> {
    await this.client.delete(`/v1/admin/platform-admins/${userId}`);
  }

  // Audit Log
  async getAuditLog(params?: {
    page?: number;
    limit?: number;
    admin_id?: string;
    action?: string;
    target_type?: string;
    since?: string;
    until?: string;
  }): Promise<PaginatedResponse<AuditEntry>> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set("page", String(params.page));
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.admin_id) qs.set("admin_id", params.admin_id);
    if (params?.action) qs.set("action", params.action);
    if (params?.target_type) qs.set("target_type", params.target_type);
    if (params?.since) qs.set("since", params.since);
    if (params?.until) qs.set("until", params.until);
    const query = qs.toString();
    return this.client.get(`/v1/admin/audit-log${query ? `?${query}` : ""}`);
  }
}
